// ===========================================
// Absence History Page
// ===========================================
// Parent page to view and manage absence notifications

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  EventBusy as AbsenceIcon,
  Add as AddIcon,
  Cancel as CancelIcon,
  Person as StudentIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { useMyChildren } from '../../hooks/useParentProfile';
import {
  useStudentAbsences,
  useCancelAbsence,
} from '../../hooks/useAbsenceNotification';
import {
  AbsenceNotification,
  getAbsenceReasonLabel,
  getAbsenceReasonColor,
  formatAbsenceDateRange,
  canCancelAbsence,
} from '../../api/absenceNotification.api';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AbsenceHistoryPage() {
  // State
  const [selectedStudentIndex, setSelectedStudentIndex] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [absenceToCancel, setAbsenceToCancel] = useState<AbsenceNotification | null>(null);

  // Queries
  const { data: children, isLoading: childrenLoading } = useMyChildren();
  const cancelAbsence = useCancelAbsence();

  // Get selected student
  const selectedStudent = children?.[selectedStudentIndex];
  const studentId = selectedStudent?.id || '';

  // Get absences for selected student
  const {
    data: absences,
    isLoading: absencesLoading,
    error: absencesError,
  } = useStudentAbsences(studentId, {
    includeCancelled: true,
  });

  // Split absences into active and cancelled
  const { activeAbsences, cancelledAbsences } = useMemo(() => {
    if (!absences) return { activeAbsences: [], cancelledAbsences: [] };

    const active = absences.filter((a) => !a.isCancelled);
    const cancelled = absences.filter((a) => a.isCancelled);

    return { activeAbsences: active, cancelledAbsences: cancelled };
  }, [absences]);

  // Handle cancel dialog
  const handleOpenCancelDialog = (absence: AbsenceNotification) => {
    setAbsenceToCancel(absence);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setAbsenceToCancel(null);
  };

  const handleConfirmCancel = async () => {
    if (!absenceToCancel) return;

    await cancelAbsence.mutateAsync(absenceToCancel.id);
    handleCloseCancelDialog();
  };

  // Render absence item
  const renderAbsenceItem = (absence: AbsenceNotification) => {
    const isCancelled = absence.isCancelled;
    const canCancel = canCancelAbsence(absence);
    const dateRange = formatAbsenceDateRange(absence.startDate, absence.endDate);

    return (
      <ListItem
        key={absence.id}
        sx={{
          bgcolor: isCancelled ? 'action.disabledBackground' : 'background.paper',
          borderRadius: 1,
          mb: 1,
          opacity: isCancelled ? 0.7 : 1,
        }}
      >
        <ListItemText
          primary={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" component="span">
                {dateRange}
              </Typography>
              <Chip
                label={getAbsenceReasonLabel(absence.reason)}
                color={getAbsenceReasonColor(absence.reason)}
                size="small"
              />
              {isCancelled && (
                <Chip
                  label="Cancelled"
                  color="default"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              {absence.reasonDetails && (
                <Typography variant="body2" color="text.secondary">
                  {absence.reasonDetails}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Reported {format(new Date(absence.createdAt), 'd MMM yyyy')} by{' '}
                {absence.submittedBy.firstName} {absence.submittedBy.lastName}
              </Typography>
              {isCancelled && absence.cancelledAt && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Cancelled {format(new Date(absence.cancelledAt), 'd MMM yyyy')}
                </Typography>
              )}
            </Box>
          }
        />
        {!isCancelled && (
          <ListItemSecondaryAction>
            {canCancel ? (
              <Tooltip title="Cancel this absence notification">
                <IconButton
                  edge="end"
                  color="error"
                  onClick={() => handleOpenCancelDialog(absence)}
                  aria-label="Cancel this absence"
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Cannot cancel past absences">
                <span>
                  <IconButton
                    edge="end"
                    disabled
                    aria-label="Cannot cancel past absences"
                  >
                    <InfoIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </ListItemSecondaryAction>
        )}
      </ListItem>
    );
  };

  // Loading state
  if (childrenLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No children state
  if (!children?.length) {
    return (
      <Box>
        <PageHeader
          title="Absence History"
          breadcrumbs={[
            { label: 'Dashboard', path: '..' },
            { label: 'Absence History' },
          ]}
        />
        <Alert severity="info">No students found in your family.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Absence History"
        breadcrumbs={[
          { label: 'Dashboard', path: '..' },
          { label: 'Absence History' },
        ]}
        action={
          <Button
            variant="contained"
            color="error"
            startIcon={<AddIcon />}
            component={Link}
            to="../report-absence"
          >
            Report Absence
          </Button>
        }
      />

      <Paper sx={{ p: 3 }}>
        {/* Student Tabs */}
        {children.length > 1 && (
          <Tabs
            value={selectedStudentIndex}
            onChange={(_, newValue) => setSelectedStudentIndex(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {children.map((child, index) => (
              <Tab
                key={child.id}
                label={`${child.firstName} ${child.lastName}`}
                icon={<StudentIcon />}
                iconPosition="start"
                id={`absence-tab-${index}`}
                aria-controls={`absence-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        )}

        {/* Content */}
        {absencesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : absencesError ? (
          <Alert severity="error">Failed to load absence history.</Alert>
        ) : activeAbsences.length === 0 && cancelledAbsences.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AbsenceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Absences Reported
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {selectedStudent?.firstName} has no reported absences.
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<AddIcon />}
              component={Link}
              to="../report-absence"
            >
              Report Absence
            </Button>
          </Box>
        ) : (
          <>
            {/* Active Absences */}
            {activeAbsences.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Active Absences ({activeAbsences.length})
                </Typography>
                <List disablePadding>
                  {activeAbsences.map(renderAbsenceItem)}
                </List>
              </Box>
            )}

            {/* Cancelled Absences */}
            {cancelledAbsences.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cancelled Absences ({cancelledAbsences.length})
                </Typography>
                <List disablePadding>
                  {cancelledAbsences.map(renderAbsenceItem)}
                </List>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
        <DialogTitle>Cancel Absence Notification?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this absence notification for{' '}
            <strong>{selectedStudent?.firstName}</strong>?
          </DialogContentText>
          {absenceToCancel && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Date:</strong>{' '}
                {formatAbsenceDateRange(absenceToCancel.startDate, absenceToCancel.endDate)}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {getAbsenceReasonLabel(absenceToCancel.reason)}
              </Typography>
            </Box>
          )}
          <DialogContentText sx={{ mt: 2 }}>
            The school will be notified and future attendance records will be updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Keep Absence</Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={cancelAbsence.isPending}
          >
            {cancelAbsence.isPending ? 'Cancelling...' : 'Cancel Absence'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
